import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/i18n";
import LoginPage from "@/pages/LoginPage";
import PlayerDashboard from "@/pages/PlayerDashboard";
import SimplePlayerDashboard from "@/pages/SimplePlayerDashboard";
import PlayerRegisterPage from "@/pages/PlayerRegisterPage";
import AdminProfilePage from "@/pages/AdminProfilePage";
import { routeConfig } from "@/config/routes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function ProtectedRoute({ evaluator, route, onLogout, setEvaluator }) {
  if (!evaluator) return <Navigate to="/" />;

  const { access, roles, component: Component, passLogout } = route;

  const isPlayer = evaluator.isPlayer;
  const userRole = evaluator.role;
  const fallback = isPlayer ? "/" : "/dashboard";

  if (access === "evaluator" && isPlayer) return <Navigate to="/" />;
  if (access === "roles" && (!roles.includes(userRole))) return <Navigate to={fallback} />;
  if (access === "admin" && userRole !== "admin") return <Navigate to={fallback} />;
  if (access === "player" && !isPlayer) return <Navigate to={fallback} />;

  const props = { evaluator };
  if (passLogout) props.onLogout = onLogout;
  if (route.passSetEvaluator) props.setEvaluator = setEvaluator;

  return <Component {...props} />;
}

function App() {
  const [evaluator, setEvaluator] = useState(() => {
    const saved = localStorage.getItem('evaluator');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (evaluator) {
      localStorage.setItem('evaluator', JSON.stringify(evaluator));
    } else {
      localStorage.removeItem('evaluator');
    }
  }, [evaluator]);

  const handleLogout = () => {
    setEvaluator(null);
  };

  return (
    <LanguageProvider>
    <ThemeProvider>
      <div className="App min-h-screen bg-background flex flex-col">
        <Toaster position="top-right" />
        <BrowserRouter>
          <div className="flex-1">
            <Routes>
              {/* Root: redirect based on role or show login */}
              <Route 
                path="/" 
                element={
                  evaluator 
                    ? <Navigate to={evaluator.isPlayer ? "/player-dashboard" : "/dashboard"} /> 
                    : <LoginPage setEvaluator={setEvaluator} />
                } 
              />

              {/* Player dashboard: special routing */}
              <Route 
                path="/player-dashboard" 
                element={
                  evaluator && evaluator.isPlayer ? (
                    evaluator.isSimplePlayer 
                      ? <SimplePlayerDashboard player={evaluator} onLogout={handleLogout} />
                      : <PlayerDashboard player={evaluator} onLogout={handleLogout} />
                  ) : <Navigate to="/" />
                } 
              />

              {/* Admin profile: needs onProfileUpdate */}
              <Route 
                path="/admin-profile" 
                element={
                  evaluator && evaluator.role === 'admin' ? (
                    <AdminProfilePage 
                      evaluator={evaluator} 
                      onLogout={handleLogout}
                      onProfileUpdate={(updated) => setEvaluator({...evaluator, ...updated})}
                    />
                  ) : <Navigate to="/dashboard" />
                } 
              />

              {/* Registration pages (public) */}
              <Route path="/register/:token" element={<PlayerRegisterPage />} />
              <Route path="/cadastro/:token" element={<PlayerRegisterPage />} />

              {/* All protected routes from config */}
              {routeConfig.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      evaluator={evaluator}
                      route={route}
                      onLogout={handleLogout}
                      setEvaluator={setEvaluator}
                    />
                  }
                />
              ))}
            </Routes>
          </div>
          {/* Footer */}
          <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/30 bg-card/30">
            <p>Desenvolvido por <span className="font-semibold text-primary">Jhonatas Valim</span></p>
          </footer>
        </BrowserRouter>
      </div>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
